/**
 * Maps dot-notation history field keys to human-readable labels.
 * Labels are sourced directly from BasicInfoForm/schema.js (which resolves
 * translations via the intl singleton at module load time).
 *
 * Used in HistoryModal to display friendly names instead of raw schema keys.
 */
import schema from '../containers/BasicInfoForm/schema';

// ─── Build label map from schema ──────────────────────────────────────────────

// Field-definition metadata keys — never schema nodes to recurse into.
const NON_FIELD_KEYS = new Set([
  'label',
  'publicLabel',
  'placeholder',
  'type',
  'required',
  'softRequired',
  'enabled',
  'visible',
  'photosEnabled',
  'surveyed',
  'isMulti',
  'options',
  'viewModeRenderOptions',
  'validation',
  'width',
]);

/**
 * Recursively walk the schema object. An entry is a field definition when it
 * has a `label` string property — record it. Some fields (e.g. section
 * headers like `ceiling_information`) carry their own `label` AND nest
 * further child fields (e.g. `front`, `back`), so we always recurse into an
 * entry's own keys afterwards rather than stopping at the first label found.
 *
 * Ambiguous child labels (e.g. `front`/`back`/`center` reused under many
 * headers) get their immediate parent's label prepended, e.g.
 * "Height From Floor To Bottom Of Deck - Back", so they read unambiguously.
 * Only the direct parent is used — labels don't compound across every
 * ancestor level.
 *
 * @param {object} obj   - current schema node
 * @param {string} prefix - dot-notation prefix accumulated so far
 * @param {object} map    - output map being built
 * @param {string} [parentLabel] - immediate parent's own label, for prefixing
 */
export function buildLabelMap(obj, prefix, map, parentLabel) {
  if (!obj || typeof obj !== 'object') return;
  Object.entries(obj).forEach(([key, value]) => {
    if (NON_FIELD_KEYS.has(key)) return;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return;
    const path = prefix ? `${prefix}.${key}` : key;
    const hasOwnLabel = typeof value.label === 'string';
    if (hasOwnLabel) {
      // Field definition — record full path and also just the leaf key
      const label = parentLabel ? `${parentLabel} - ${value.label}` : value.label;
      map[path] = label;
      map[key] = map[key] || label; // leaf key wins first definition
    }
    // Recurse regardless of whether this node had a label, since it may
    // also contain nested child field definitions of its own. Children are
    // prefixed with this node's own (unprefixed) label, not the compounded one.
    buildLabelMap(value, path, map, hasOwnLabel ? value.label : parentLabel);
  });
}

const LABEL_MAP = {};
// schema.basic_info contains all the field definitions
if (schema && schema.basic_info) {
  buildLabelMap(schema.basic_info, '', LABEL_MAP);
}

// Manual additions for upload fields and non-basic_info fields not in the schema
Object.assign(LABEL_MAP, {
  status:               'Status',
  photos:               'Images for QA',
  floorplans:           'Images for CAD',
  featured_upload:      'Storefront Image',
  floorplan_upload:     'Featured Floorplan',
  pdf_upload:           'LOD PDF',
  profile_pdf_upload:   'Profile PDF',
  autocad_upload:       'AutoCAD File',
});

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Convert a snake_case or dot-notation key to Title Case as fallback.
 * @param {string} key
 * @returns {string}
 */
function titleCase(key) {
  const leaf = key.includes('.') ? key.split('.').pop() : key;
  return leaf
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Return a human-readable label for a dot-notation history field key.
 *
 * Lookup order:
 *   1. Exact match in LABEL_MAP (after stripping "basic_info." prefix)
 *   2. Leaf key only
 *   3. Title-cased leaf as fallback
 *
 * @param {string} fieldName - dot-notation key (e.g. "basic_info.ceiling_information.clear_ceiling_height")
 * @returns {string}
 */
export function getFieldLabel(fieldName) {
  if (!fieldName) return fieldName;

  // Strip leading "basic_info." prefix
  const stripped = fieldName.startsWith('basic_info.')
    ? fieldName.slice('basic_info.'.length)
    : fieldName;

  // 1. Exact match (full path without basic_info prefix)
  if (LABEL_MAP[stripped]) return LABEL_MAP[stripped];

  // 2. Leaf key
  const leaf = stripped.includes('.') ? stripped.split('.').pop() : stripped;
  if (LABEL_MAP[leaf]) return LABEL_MAP[leaf];

  // 3. Fallback: title-case the leaf segment
  return titleCase(leaf);
}

/**
 * Return both label and raw key (for tooltip/debug display).
 * @param {string} fieldName
 * @returns {{ label: string, key: string }}
 */
export function getFieldLabelWithKey(fieldName) {
  return { label: getFieldLabel(fieldName), key: fieldName };
}
